import { useEffect } from "react";
import {
  absoluteUrl,
  buildPageTitle,
  DEFAULT_OG_IMAGE_PATH,
  getSiteUrl,
  truncateMetaDescription
} from "../lib/seo";

const JSON_LD_SCRIPT_ID = "panetteria-seo-jsonld";

function upsertMeta(attributeName, attributeValue, content) {
  let meta = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attributeName, attributeValue);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

function syncJsonLd(serializedJsonLd) {
  const existing = document.getElementById(JSON_LD_SCRIPT_ID);

  if (!serializedJsonLd) {
    existing?.remove();
    return;
  }

  const script = existing || document.createElement("script");
  script.id = JSON_LD_SCRIPT_ID;
  script.type = "application/ld+json";
  script.textContent = serializedJsonLd;

  if (!existing) {
    document.head.appendChild(script);
  }
}

const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE_PATH,
  type = "website",
  robots = "index,follow",
  jsonLd = null
}) => {
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const siteUrl = getSiteUrl();
    const canonicalUrl = absoluteUrl(path, siteUrl);
    const metaTitle = buildPageTitle(title);
    const metaDescription = truncateMetaDescription(description);
    const imageUrl = absoluteUrl(image || DEFAULT_OG_IMAGE_PATH, siteUrl);

    document.title = metaTitle;
    upsertCanonical(canonicalUrl);
    upsertMeta("name", "description", metaDescription);
    upsertMeta("name", "robots", robots);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:title", metaTitle);
    upsertMeta("property", "og:description", metaDescription);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:locale", "it_IT");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:url", canonicalUrl);
    upsertMeta("name", "twitter:title", metaTitle);
    upsertMeta("name", "twitter:description", metaDescription);
    upsertMeta("name", "twitter:image", imageUrl);

    syncJsonLd(serializedJsonLd);
  }, [description, image, path, robots, serializedJsonLd, title, type]);

  return null;
};

export default Seo;
